import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FinancierService {
  private financiers = new BehaviorSubject<Map<string, string[]>>(
    new Map<string, string[]>(),
  );
  financiers$ = this.financiers.asObservable();
  private newFinanciers: Map<string, Set<string>> = new Map<
    string,
    Set<string>
  >();

  private financierAddedSubject = new Subject<{
    worksiteId: string;
    financierId: string;
  }>();
  financierAdded$ = this.financierAddedSubject.asObservable();

  public selectForWorksite(worksiteId: string): Observable<string[]> {
    return this.financiers$.pipe(map((f) => f.get(worksiteId) ?? []));
  }

  public getFinanciers(worksiteId: string): string[] {
    return this.financiers.value.get(worksiteId) ?? [];
  }

  public addWorksite(worksiteId: string, financiers: string[]): void {
    const financiersValue = this.financiers.value;
    financiersValue.set(worksiteId, financiers);
    this.financiers.next(financiersValue);
  }

  public removeWorksite(worksiteId: string): void {
    const financiersValue = this.financiers.value;
    financiersValue.delete(worksiteId);
    this.financiers.next(financiersValue);
  }

  public addFinancier(worksiteId: string, financier: string): void {
    const financiersValue = this.financiers.value;
    const financiersWorksite = financiersValue.get(worksiteId);
    if (!financiersWorksite) {
      return;
    }
    financiersWorksite.push(financier);
    financiersValue.set(worksiteId, financiersWorksite);
    this.financiers.next(financiersValue);

    this.financierAddedSubject.next({ worksiteId, financierId: financier });
  }

  public removeFinancier(worksiteId: string, financier: string): void {
    const financiersValue = this.financiers.value;
    let financiersWorksite = financiersValue.get(worksiteId);
    if (!financiersWorksite) {
      return;
    }
    financiersWorksite = financiersWorksite.filter((f) => f !== financier);
    financiersValue.set(worksiteId, financiersWorksite);
    this.financiers.next(financiersValue);

    const newFinanciersSet = this.newFinanciers.get(worksiteId);
    if (newFinanciersSet) {
      newFinanciersSet.delete(financier);
    }
  }

  public addNewFinanciers(worksiteId: string, financierIds: string[]) {
    let newFinanciers = this.newFinanciers.get(worksiteId);

    if (!newFinanciers) {
      newFinanciers = new Set<string>();
    }

    for (const id of financierIds) {
      newFinanciers.add(id);
    }

    this.newFinanciers.set(worksiteId, newFinanciers);
  }

  public addNewFinancier(worksiteId: string, financierId: string) {
    let newFinanciers = this.newFinanciers.get(worksiteId);

    if (!newFinanciers) {
      newFinanciers = new Set<string>();
    }

    newFinanciers.add(financierId);

    this.newFinanciers.set(worksiteId, newFinanciers);
  }

  public getNewFinanciers(worksiteId: string) {
    return this.newFinanciers.get(worksiteId) ?? new Set<string>();
  }

  public resetFinanciers() {
    this.financiers = new BehaviorSubject<Map<string, string[]>>(
      new Map<string, string[]>(),
    );
    this.newFinanciers = new Map<string, Set<string>>();
    this.financiers$ = this.financiers.asObservable();
  }
}
